import { useMemo, useContext } from 'react';
import { Context } from '../../../../../Context';

/**
 * Custom hook for preparing compartment data and metadata
 * @param {Object} apartmentData - Full apartment data object
 * @returns {Object} - Helper functions and computed data
 */
export function useCompartmentData(apartmentData) {
	const { compartmentsDictionary } = useContext(Context);

	/**
	 * Get arrangement versions array for all compartments
	 */
	const arrangementVersions = useMemo(() => {
		if (!apartmentData?.compartments) return [];

		const sortedCompartments = [...apartmentData.compartments].sort((a, b) => {
			return parseInt(a.number, 10) - parseInt(b.number, 10);
		});

		return sortedCompartments.map((compartment) => compartment.versions);
	}, [apartmentData]);

	/**
	 * Get total number of possible arrangements
	 */
	const numberOfPossibleArrangements = useMemo(() => {
		return arrangementVersions.reduce((acc, num) => acc * num, 1);
	}, [arrangementVersions]);

	/**
	 * Get compartment name from dictionary or fallback to original
	 * @param {Object} compartment - Compartment object
	 * @returns {string} - Compartment name
	 */
	const getCompartmentName = (compartment) => {
		const compartmentNameData = compartmentsDictionary?.find(
			(comp) => comp.id === compartment.name_cad
		);

		return compartmentNameData
			? compartmentNameData.name_pl
			: compartment.name_original;
	};

	/**
	 * Get compartment name for outdoor compartments (different key)
	 * @param {Object} compartment - Compartment object
	 * @returns {string} - Compartment name
	 */
	const getOutdoorCompartmentName = (compartment) => {
		const compartmentNameData = compartmentsDictionary?.compartments?.find(
			(comp) => comp.id === compartment.name
		);

		return compartmentNameData
			? compartmentNameData.name_pl
			: compartment.name;
	};

	/**
	 * Get area for a compartment
	 * @param {Object} compartment - Compartment object
	 * @returns {number} - Area value
	 */
	const getCompartmentArea = (compartment) => {
		return compartment.area_original;
	};

	/**
	 * Check if compartment is interactive (has versions in arrange mode)
	 * @param {Object} compartment - Compartment object
	 * @returns {boolean}
	 */
	const isCompartmentInteractive = (compartment) => {
		const versions = arrangementVersions[compartment.number - 1];
		return versions > 1;
	};

	/**
	 * Get outdoor compartment IDs from dictionary
	 * @returns {Array<string>}
	 */
	const getOutdoorCompartmentIds = () => {
		return compartmentsDictionary
			?.filter((room) => room.group === 'OutdoorSpace')
			.map((room) => room.id) || [];
	};

	return {
		arrangementVersions,
		numberOfPossibleArrangements,
		getCompartmentName,
		getOutdoorCompartmentName,
		getCompartmentArea,
		isCompartmentInteractive,
		getOutdoorCompartmentIds,
	};
}
